import React, { useState } from 'react';
import {
    Box,
    Text,
    useColorModeValue,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    VStack,
    HStack,
    Badge,
    Input,
    Select,
    IconButton,
    Tooltip,
    Spinner,
    Alert,
    AlertIcon,
    Flex,
    Avatar,
    SimpleGrid,
    InputGroup,
    InputLeftElement,
    Button,
    FormControl,
    FormLabel,
    Grid,
} from '@chakra-ui/react';

// Custom components
import Card from 'components/card/Card';
import CustomPagination from 'components/pagination/Pagination';
import { RefreshCw, Search, Calendar } from 'lucide-react';
import { TrainerClient } from '../../../services/trainerService';
import { useTrainerClients } from '../../../services/useTrainerClients';
import { useNavigate } from 'react-router-dom';

export default function TrainerClientsList() {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilter, setActiveFilter] = useState<string>('all');

    const {
        clients,
        isLoading,
        totalCount,
        currentPage,
        totalPages,
        setCurrentPage,
        refreshClients,
        fetchClients
    } = useTrainerClients({
        page: 1,
        limit: 10,
        active_only: activeFilter === 'active' ? true : activeFilter === 'inactive' ? false : undefined,
        autoFetch: true
    });

    const textColor = useColorModeValue('navy.700', 'white');
    const textColorSecondary = useColorModeValue('gray.600', 'gray.400');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const bgColor = useColorModeValue('white', 'navy.700');
    const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');

    // Handle filter changes
    const handleActiveFilterChange = (newFilter: string) => {
        setActiveFilter(newFilter);
        fetchClients({
            page: 1,
            active_only: newFilter === 'active' ? true : newFilter === 'inactive' ? false : undefined
        });
    };

    const filteredClients = clients.filter(client =>
        client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Handle calendar navigation
    const handleViewCalendar = (client: TrainerClient) => {
        navigate(`/trainer/calendar/${client.id}`);
    };

    return (
        <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
            <Card 
                bg={bgColor} 
                borderRadius="20px"
                boxShadow="0 20px 40px -12px rgba(0,0,0,0.1)"
                border="1px solid"
                borderColor={useColorModeValue("gray.100", "gray.700")}
                overflow="hidden"
                p={{ base: 4, md: 6 }}
            >
                <Flex direction="column" w="100%">
                    {/* Header */}
                    <Flex 
                        justify="space-between" 
                        align={{ base: "start", md: "center" }}
                        direction={{ base: "column", md: "row" }}
                        gap={{ base: 4, md: 0 }}
                        mb="20px"
                    >
                        <Text color={textColor} fontSize={{ base: "lg", md: "22px" }} fontWeight="700">
                            My Clients
                        </Text>
                        <Flex justify="flex-end" align="center">
                            <HStack spacing={3}>
                                <Button
                                    leftIcon={<RefreshCw size={16} />}
                                    variant="outline"
                                    onClick={refreshClients}
                                    isLoading={isLoading}
                                    loadingText="Refreshing"
                                >
                                    Refresh
                                </Button>
                            </HStack>
                        </Flex>
                    </Flex>

                    {/* Filters */}
                    <Grid
                        templateColumns={{
                            base: "1fr",
                            md: "2fr 1fr",
                            lg: "2fr 1fr 1fr",
                        }}
                        gap="20px"
                        mb="20px"
                    >
                        <InputGroup>
                            <InputLeftElement pointerEvents="none">
                                <Search size={16} color="gray" />
                            </InputLeftElement>
                            <Input
                                placeholder="Search clients..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                bg={bgColor}
                                border="1px solid"
                                borderColor={borderColor}
                                color={textColor}
                                _placeholder={{ color: textColorSecondary }}
                                _focus={{
                                    borderColor: "brand.500",
                                    boxShadow: "0 0 0 1px brand.500",
                                }}
                                _hover={{
                                    borderColor: useColorModeValue("gray.300", "navy.500"),
                                }}
                            />
                        </InputGroup>

                        <Select
                            placeholder="All Status"
                            value={activeFilter}
                            onChange={(e) => {
                                handleActiveFilterChange(e.target.value);
                            }}
                            bg={bgColor}
                            border="1px solid"
                            borderColor={borderColor}
                            color={textColor}
                            _focus={{
                                borderColor: "brand.500",
                                boxShadow: "0 0 0 1px brand.500",
                            }}
                            _hover={{
                                borderColor: useColorModeValue("gray.300", "navy.500"),
                            }}
                        >
                            <option value="all">All Clients</option>
                            <option value="active">Active Only</option>
                            <option value="inactive">Inactive Only</option>
                        </Select>

                        {/* Stats */}
                        <HStack spacing={4} justify="flex-end" display={{ base: 'none', lg: 'flex' }}>
                            <Box textAlign="center">
                                <Text fontSize="lg" fontWeight="bold" color="green.500">
                                    {filteredClients.filter(c => c.is_active).length}
                                </Text>
                                <Text fontSize="xs" color={textColorSecondary}>
                                    Active
                                </Text>
                            </Box>
                            <Box textAlign="center">
                                <Text fontSize="lg" fontWeight="bold" color="red.500">
                                    {filteredClients.filter(c => !c.is_active).length}
                                </Text>
                                <Text fontSize="xs" color={textColorSecondary}>
                                    Inactive
                                </Text>
                            </Box>
                        </HStack>
                    </Grid>

                    {/* Table Content */}
                    {isLoading ? (
                        <Flex justify="center" align="center" minH="200px">
                            <Spinner size="xl" />
                        </Flex>
                    ) : filteredClients.length > 0 ? (
                        <>
                            <TableContainer>
                                <Table variant="simple" color={textColorSecondary} mb="24px" mt="12px">
                                    <Thead>
                                        <Tr>
                                            <Th
                                                pe="10px"
                                                borderColor={borderColor}
                                                fontSize={{ sm: '10px', lg: '12px' }}
                                                color="gray.400"
                                            >
                                                Client
                                            </Th>
                                            <Th
                                                pe="10px"
                                                borderColor={borderColor}
                                                fontSize={{ sm: '10px', lg: '12px' }}
                                                color="gray.400"
                                            >
                                                Contact
                                            </Th>
                                            <Th
                                                pe="10px"
                                                borderColor={borderColor}
                                                fontSize={{ sm: '10px', lg: '12px' }}
                                                color="gray.400"
                                            >
                                                Gender
                                            </Th>
                                            <Th
                                                pe="10px"
                                                borderColor={borderColor}
                                                fontSize={{ sm: '10px', lg: '12px' }}
                                                color="gray.400"
                                            >
                                                Join Date
                                            </Th>
                                            <Th
                                                pe="10px"
                                                borderColor={borderColor}
                                                fontSize={{ sm: '10px', lg: '12px' }}
                                                color="gray.400"
                                            >
                                                Status
                                            </Th>
                                            <Th
                                                pe="10px"
                                                borderColor={borderColor}
                                                fontSize={{ sm: '10px', lg: '12px' }}
                                                color="gray.400"
                                            >
                                                Actions
                                            </Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {filteredClients.map((client) => (
                                            <Tr 
                                                key={client.id}
                                                _hover={{ bg: hoverBg }}
                                            >
                                                <Td
                                                    fontSize={{ sm: '14px' }}
                                                    minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                                    borderColor="transparent"
                                                >
                                                    <HStack spacing={3}>
                                                        <Avatar
                                                            name={client.name}
                                                            size="sm"
                                                            bg="blue.500"
                                                            color="white"
                                                        />
                                                        <Text fontWeight="500" color={textColor}>
                                                            {client.name}
                                                        </Text>
                                                    </HStack>
                                                </Td>
                                                <Td
                                                    fontSize={{ sm: '14px' }}
                                                    minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                                    borderColor="transparent"
                                                >
                                                    <VStack align="start" spacing={1}>
                                                        <Text fontSize="sm" color={textColor} fontWeight="400">
                                                            {client.email}
                                                        </Text>
                                                        {client.phone && (
                                                            <Text fontSize="sm" color={textColorSecondary}>
                                                                {client.phone}
                                                            </Text>
                                                        )}
                                                    </VStack>
                                                </Td>
                                                <Td
                                                    fontSize={{ sm: '14px' }}
                                                    minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                                    borderColor="transparent"
                                                >
                                                    <Text fontSize="sm" color={textColor} fontWeight="400">
                                                        {client.gender || 'Not provided'}
                                                    </Text>
                                                </Td>
                                                <Td
                                                    fontSize={{ sm: '14px' }}
                                                    minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                                    borderColor="transparent"
                                                >
                                                    <Text fontSize="sm" color={textColorSecondary} fontWeight="400">
                                                        {new Date(client.join_date).toLocaleDateString()}
                                                    </Text>
                                                </Td>
                                                <Td
                                                    fontSize={{ sm: '14px' }}
                                                    minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                                    borderColor="transparent"
                                                >
                                                    <Badge
                                                        colorScheme={client.is_active ? 'green' : 'red'}
                                                        variant="solid"
                                                        fontSize="xs"
                                                    >
                                                        {client.is_active ? 'Active' : 'Inactive'}
                                                    </Badge>
                                                </Td>
                                                <Td
                                                    fontSize={{ sm: '14px' }}
                                                    minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                                    borderColor="transparent"
                                                >
                                                    <Button
                                                        size="sm"
                                                        leftIcon={<Calendar size={14} />}
                                                        colorScheme="blue"
                                                        variant="outline"
                                                        onClick={() => handleViewCalendar(client)}
                                                    >
                                                        Calendar
                                                    </Button>
                                                </Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                            
                            {/* Custom Pagination */}
                            <CustomPagination 
                                totalResults={totalCount} 
                                pageSize={10}
                                currentPage={currentPage - 1} // Convert 1-based to 0-based
                                onPageChange={(newPageIndex: number) => {
                                    setCurrentPage(newPageIndex + 1); // Convert 0-based to 1-based
                                }}
                            />
                        </>
                    ) : (
                        <Flex justify="center" align="center" minH="200px">
                            <VStack spacing="20px">
                                <Text color={textColor} fontSize="18px">
                                    {searchTerm ? 'No clients match your search criteria.' : 'You have no clients assigned yet.'}
                                </Text>
                            </VStack>
                        </Flex>
                    )}
                </Flex>
            </Card>
        </Box>
    );
}
